import {
  Box,
  CircularProgress,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { useDAL } from '@/contexts/useDAL';

import SelectPlaceholder from '../selectPlaceholder';

interface GcpEncryptionComponentProps {
  cloudAccountId?: string;
  regionName?: string;
  onChange: (encryptionKeyName?: string) => void;
  initialEncryptionKeyName?: string;
  title?: string;
  canBeTurnedOff?: boolean;
}

export const GcpEncryptionComponent = ({
  cloudAccountId,
  regionName,
  onChange,
  initialEncryptionKeyName,
  title = 'Encrypt this resource',
  canBeTurnedOff = false,
}: GcpEncryptionComponentProps) => {
  const [isEncrypted, setIsEncrypted] = useState(
    !!initialEncryptionKeyName || !canBeTurnedOff
  );
  const [selectedKeyName, setSelectedKeyName] = useState<string | undefined>(
    initialEncryptionKeyName
  );

  const dal = useDAL();

  const { isLoading, data: body } = dal.restore.gcpVm.listEncryptionKeys(
    cloudAccountId,
    regionName
  );

  const handleEncryptionToggle = () => {
    const newIsEncrypted = !isEncrypted;
    setIsEncrypted(newIsEncrypted);
    if (!newIsEncrypted) {
      onChange(undefined);
    } else {
      onChange(selectedKeyName);
    }
  };

  const handleKeyChange = (newKeyName: string | undefined) => {
    if (newKeyName === selectedKeyName) {
      return;
    }

    setSelectedKeyName(newKeyName);
    if (isEncrypted) {
      onChange(newKeyName);
    }
  };

  const isDisabled =
    isLoading ||
    !cloudAccountId ||
    !regionName ||
    (body && (!body.keys || body.keys.length === 0));

  return (
    <Box className='mx-[40px] my-[24px]'>
      <Box className='flex justify-between items-center'>
        <Typography className='font-semibold' variant='body1'>
          {title}
        </Typography>
        {canBeTurnedOff && (
          <Switch
            size='small'
            checked={isEncrypted}
            onChange={handleEncryptionToggle}
            data-testid='gcp-encryption-toggle'
          />
        )}
      </Box>
      {isEncrypted && (
        <Box className='mt-[20px]'>
          <FormControl size='small' className='w-full'>
            <FormLabel>Customer-managed encryption key (CMEK)</FormLabel>
            <Box className='flex items-center gap-[36px] mt-[8px]'>
              <Box className='w-1/2'>
                <Select
                  data-testid='gcp-encryption-key-select'
                  displayEmpty
                  value={selectedKeyName || ''}
                  onChange={(event) => {
                    event.stopPropagation();
                    handleKeyChange(event.target.value);
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  disabled={isDisabled}
                  renderValue={(value: string) => {
                    if (isLoading && cloudAccountId && regionName) {
                      return <CircularProgress size={20} />;
                    }
                    if (!value) {
                      let title = 'Select';
                      if (!regionName) {
                        title = 'Region must be selected';
                      } else if (
                        body &&
                        (!body.keys || body.keys.length === 0)
                      ) {
                        title = 'No keys found';
                      }
                      return <SelectPlaceholder title={title} />;
                    }
                    return parseGcpKeyName(value);
                  }}
                  className='w-full'
                >
                  {body?.keys?.map((key) => (
                    <MenuItem
                      key={key.name}
                      value={key.name}
                      data-testid={`gcp-encryption-key-option-${key.name}`}
                    >
                      {parseGcpKeyName(key.name)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};

// Helper function to parse GCP key name
const parseGcpKeyName = (fullKeyName: string): string => {
  const parts = fullKeyName.split('/');
  if (
    parts.length >= 8 &&
    parts[0] === 'projects' &&
    parts[2] === 'locations' &&
    parts[4] === 'keyRings' &&
    parts[6] === 'cryptoKeys'
  ) {
    const cryptoKeyName = parts[7];
    const keyRingName = parts[5];
    const locationName = parts[3];
    return `${cryptoKeyName} (Ring: ${keyRingName}, Loc: ${locationName})`;
  }
  return fullKeyName; // Fallback to full name if parsing fails
};
